import { FunctionComponent, useState, useEffect } from "react";
import {
  useToast,
  ToastType,
} from "../../../../../context_providers/toast/toast_ctx";
import {
  inwardDefaultValueV2,
  InwardModel,
  itemMovementDefaultValueV2,
  ItemMovementModelV2,
} from "../../../../inventory/models/inventory_voucher";
import ModalDialog from "../../../../../ui/new_components/common/modal";

interface ItemRowProps {
  max_qty?: number;
  savedItem?: ItemMovementModelV2;
  index: number;
  item?: InwardModel;
  onSubmit: (data: ItemMovementModelV2) => void;
  edit: boolean;
  disabled: boolean;
  onDelete?: (i: ItemMovementModelV2) => void;
  projectDetails?: any;
  unit_price?: number;
}

const SRNItemRow: FunctionComponent<ItemRowProps> = (props) => {
  const [existingItem, setExistingItem] = useState<InwardModel>({
    ...inwardDefaultValueV2,
  });
  const [newItem, setNewItem] = useState<ItemMovementModelV2>({
    ...itemMovementDefaultValueV2,
  });
  const [selected, setSelected] = useState(false);

  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessages, setErrorMessages] = useState<string | null>(null);
  const { showToast } = useToast();
  const { edit, onSubmit } = props;
  const [editable, setEditable] = useState(false);

  useEffect(() => {
    setEditable(edit);
  }, [edit]);

  useEffect(() => {
    setExistingItem({ ...(props.item || inwardDefaultValueV2) });

    if (props.savedItem) {
      setNewItem(props.savedItem);
      setSelected(true);
    } else {
      setNewItem({ ...(props.item || itemMovementDefaultValueV2) });
      setSelected(false);
    }
  }, [props.item, props.savedItem]);

  useEffect(() => {
    if (
      selected &&
      newItem.received_unit_no !== undefined &&
      newItem.received_unit_no > 0
    ) {
      onSubmit(newItem);
    }
  }, [selected, newItem.received_unit_no]);

  const remainingQty =
    (existingItem?.total_ordered_qty ?? 0) -
    (existingItem?.total_received_qty ?? 0);

  return (
    <>
      {showErrorModal && (
        <ModalDialog
          show={showErrorModal}
          title="Import Errors"
          onClose={() => setShowErrorModal(false)}
        >
          <div className="p-4">
            <pre className="bg-gray-100 p-2 rounded text-red-600 whitespace-pre-wrap">
              {errorMessages}
            </pre>
          </div>
        </ModalDialog>
      )}
      <div
        className={`border-2 rounded-xl p-5 mb-4 transition
    ${selected ? "border-blue-500 bg-blue-50" : "border-gray-300 bg-white"}
  `}
      >
        {/* Header */}
        <div className="flex justify-between items-start gap-2">
          <div className="flex gap-4 min-w-0">
            {/* Checkbox */}

            {!props.disabled && (
              <input
                type="checkbox"
                checked={selected}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setSelected(checked);

                  if (!checked) {
                    onSubmit({ ...newItem, received_unit_no: 0 }); // remove from parent
                  }
                }}
              />
            )}

            {/* Item Info */}
            <div className="min-w-0">
              <div className="text-lg font-semibold text-gray-900 break-words">
                {existingItem?.item?.name}
              </div>
              <div className="text-sm text-gray-700">
                Code: {existingItem?.item?.item_code}
              </div>
              <div className="text-sm text-gray-700">
                Price: {props.unit_price ?? (existingItem as any)?.price ?? 0}
              </div>
              <div className="text-sm text-gray-600">
                Description: {existingItem?.item?.description}
              </div>
              <div className="text-sm text-gray-600">
                Remaining: {remainingQty} {existingItem?.item?.unit?.symbol}
              </div>
            </div>
          </div>

          {/* Rate */}
          <div className="text-lg font-semibold text-gray-900">
            {existingItem?.item?.unit?.symbol}
          </div>
        </div>

        {/* Divider */}
        <div className="border-t my-4" />

        {/* Quantity Completed */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
          <span className="text-base text-gray-700">Quantity Completed:</span>

          <input
            type="number"
            disabled={!selected}
            min={0}
            max={remainingQty}
            value={newItem?.received_unit_no || ""}
            onChange={(e) => {
              let val = Number(e.target.value);

              // 🚫 Prevent exceeding remaining
              if (val > remainingQty) {
                val = remainingQty;
                showToast({
                  type: ToastType.warning,
                  text: `Quantity cannot exceed remaining (${remainingQty})`,
                });
              }

              setNewItem((prev) => ({
                ...prev,
                received_unit_no: val,
              }));
            }}
            className={`w-full sm:w-40 px-3 py-2 rounded-md text-lg border focus:outline-none focus:ring-2 ring-blue-400
        ${
          selected
            ? "bg-white text-gray-900"
            : "bg-gray-100 text-gray-400 cursor-not-allowed"
        }
      `}
          />
        </div>
      </div>
    </>
  );
};

export default SRNItemRow;
